// ============================================================
// Die Feier (§16.3) — das Blatt, das aufgeht, wenn eine EIGENE Handlung
// einen Erfolg erzeugt hat: eine Abgabe abgegeben, ein Projekt angelegt.
//
// Drei Festlegungen, und alle drei stehen so in SPEC §16.3:
//
// 1. NUR BEI EIGENEN HANDLUNGEN. Ausgelöst wird sie in `api/hooks.ts`, wo die
//    Mutation zurückkommt — nie beim bloßen Lesen des Feeds. Wer morgens die
//    Erfolge der Woche durchsieht, bekommt kein Konfetti.
// 2. EIN AUSBRUCH, UNTER ZWEI SEKUNDEN. Die Flugbahnen werden EINMAL je
//    Feier gerechnet (useMemo am Titel) — würden sie bei jedem Anstrich neu
//    gewürfelt, liefe die Bewegung endlos von vorn los.
// 3. KEIN NEUES PAKET. 28 Rechtecke mit je einem eigenen Zielpunkt sind CSS
//    und sonst nichts (aktuelles.css, `.ak-konf`). Bei „Bewegung reduzieren"
//    fällt der Flug ganz weg und es bleibt ein Einblenden.
//
// Das Blatt schließt sich nach vier Sekunden von selbst oder auf einen Klick.
// Absichtlich KEIN Modal aus `components/ui`: es fängt den Fokus ein und
// sperrt das Rollen, und eine Feier soll niemanden aufhalten — sie soll
// auffallen und dann verschwinden.
// ============================================================
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Icon, portalRoot } from "../ui";
import { useBewegungAus } from "../../hooks/useMedienabfrage";
import { useTexte } from "../../screens/aktuelles/texte";
import { useUi } from "../../store/ui";
import "../../screens/aktuelles/aktuelles.css";

/** Wie lange das Blatt stehen bleibt, wenn niemand klickt. */
const DAUER_MS = 4000;

/** Die Farben der Entwurfsleinwand (Erfolg.dc.html) — sie hängen nicht am Thema. */
const KONFETTI_FARBEN = ["#014351", "#447973", "#b2d95e", "#d96369", "#5ebdef", "#e5bfc4"];
const KONFETTI_ZAHL = 28;

interface Schnipsel {
  farbe: string;
  dx: number;
  dy: number;
  rot: number;
  verzug: number;
}

/**
 * Ein Ring aus Schnipseln, gleichmäßig verteilt und in fünf Weiten gestaffelt.
 * Kein Zufall: dieselbe Feier sieht dann zweimal gleich aus, und das ist bei
 * einer Bewegung, die 1,8 Sekunden dauert, ein Vorteil — sie wirkt gesetzt
 * und nicht wie ein Fehler, der zufällig anders ausfällt.
 */
function schnipsel(): Schnipsel[] {
  const raus: Schnipsel[] = [];
  for (let i = 0; i < KONFETTI_ZAHL; i += 1) {
    const winkel = (i / KONFETTI_ZAHL) * Math.PI * 2;
    const weite = 180 + (i % 5) * 42;
    raus.push({
      farbe: KONFETTI_FARBEN[i % KONFETTI_FARBEN.length] ?? KONFETTI_FARBEN[0]!,
      dx: Math.round(Math.cos(winkel) * weite),
      dy: Math.round(Math.sin(winkel) * weite * 0.75 + 120),
      rot: 180 + (i % 7) * 60,
      verzug: (i % 6) * 40,
    });
  }
  return raus;
}

export function Erfolg() {
  const feier = useUi((s) => s.erfolg);
  const feiern = useUi((s) => s.feiern);
  const t = useTexte();
  const ruhig = useBewegungAus();

  // Der Titel als Abhängigkeit und nicht das Objekt: zwei Feiern hintereinander
  // sollen zwei Ausbrüche sein, ein Neuzeichnen derselben aber keiner.
  const titel = feier?.titel ?? null;
  const flug = useMemo(() => (titel === null || ruhig ? [] : schnipsel()), [titel, ruhig]);

  useEffect(() => {
    if (!feier) return;
    const uhr = window.setTimeout(() => feiern(null), DAUER_MS);
    return () => window.clearTimeout(uhr);
  }, [feier, feiern]);

  if (!feier) return null;

  return createPortal(
    <div className="ak-feier anim-fade" role="status" aria-live="polite">
      {/* Der Hof ist ein Knopf: ein Klick irgendwohin nimmt das Blatt weg. */}
      <button type="button" className="ak-feier-hof" aria-label={t.feier.schliessen} onClick={() => feiern(null)} />
      {flug.map((s, i) => (
        <span
          key={i}
          className="ak-konf"
          aria-hidden="true"
          style={{
            background: s.farbe,
            animationDelay: `${s.verzug}ms`,
            ["--ak-dx" as string]: `${s.dx}px`,
            ["--ak-dy" as string]: `${s.dy}px`,
            ["--ak-rot" as string]: `${s.rot}deg`,
          }}
        />
      ))}
      <div className="glass-strong ak-feier-karte anim-pop">
        <span className="ak-feier-haken" aria-hidden="true">
          {/* Ein Abzeichen (§32.6) bekommt die Medaille statt des Hakens: es ist
              keine erledigte Handlung, sondern ihre Folge. */}
          <Icon name={feier.art === "abzeichen" ? "medal" : "check"} size={30} weight="bold" />
        </span>
        <span className="lbl">{t.feier[feier.art]}</span>
        <h2 className="ak-feier-titel">{feier.titel}</h2>
        {feier.untertitel ? <span className="ak-feier-unter">{feier.untertitel}</span> : null}
      </div>
    </div>,
    portalRoot(),
  );
}
